import React from 'react';
import FloatingShapes from "../common/FloatingShapes";

const medicalSafetyItems = [
  "24/7 on-site medical staff and fully equipped infirmary",
  "Emergency response protocols and first-aid-trained counselors",
  "Strict health and hygiene standards maintained throughout the complex",
  "Regular safety drills and comprehensive risk management procedures",
];

const facilitiesItems = [
  "Sports fields and courts (basketball, volleyball, football)",
  "Swimming pool and water activities area",
  "Arts and crafts studios",
  "Performance stage and amphitheater",
  "Nature trails and outdoor exploration areas",
  "Library and learning resource center",
  "Indoor recreation and game rooms",
];

const Facilities = () => {
  return (
    <>
      {/* ───────────────── Hero Section (Dark) ───────────────── */}
      <section className="relative overflow-hidden bg-primary-800 -mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-primary-600/20 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] rounded-full bg-secondary-500/15 blur-[120px]" />
          <div className="absolute -bottom-20 right-1/3 w-[400px] h-[400px] rounded-full bg-primary-400/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="default" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative min-h-[60vh] pt-40 pb-16 sm:pt-48 sm:pb-20 flex items-center">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
            {/* Badge */}
            <div className="flex justify-center mb-10 animate-fade-in">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-primary-300 backdrop-blur-sm">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-secondary-400 animate-pulse" />
                Services & Facilities
              </div>
            </div>

            {/* Heading */}
            <h1 className="text-center text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-6 animate-fade-in">
              <span className="bg-gradient-to-r from-primary-400 via-secondary-400 to-primary-300 bg-clip-text text-transparent">
                Services & Facilities
              </span>
            </h1>
            <p className="text-center text-lg text-slate-400 max-w-3xl mx-auto animate-fade-in">
              Everything you need to know about our comprehensive services and
              world-class facilities.
            </p>
          </div>
        </div>
      </section>

      {/* ───────────────── Introduction & Staff (White) ───────────────── */}
      <section className="relative bg-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -left-32 w-[400px] h-[400px] rounded-full bg-primary-100/60 blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[350px] rounded-full bg-secondary-100/50 blur-[80px]" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto animate-slide-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-sm font-medium text-primary-700 ring-1 ring-primary-200/50 mb-6">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
              Overview
            </div>

            <p className="text-lg leading-8 text-neutral-600 mb-8">
              At the International Children's Complex Nairamdal, we provide a
              safe, comfortable, and well-equipped environment for children from
              Mongolia and around the world. Our facilities and services are
              designed to support enriching camp experiences that promote
              learning, friendship, and personal growth.
            </p>

            <div className="border-l-4 border-primary-500 bg-neutral-50 rounded-xl p-6 pl-8">
              <h3 className="font-bold text-lg text-neutral-900 mb-2">
                Our Staff
              </h3>
              <p className="text-neutral-600 leading-relaxed">
                Our dedicated team of experienced professionals includes camp
                counselors, educators, medical personnel, catering staff, and
                administrative support. All staff members undergo thorough
                background checks and training in child safety, first aid, and
                program facilitation to ensure the highest standards of care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Accommodation (Dark) ───────────────── */}
      <section className="relative overflow-hidden bg-primary-800">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-secondary-500/15 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-warm-500/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="alt" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14 animate-fade-in">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Accommodation
              </h2>
              <div className="mx-auto h-1 w-24 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500" />
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Main Building */}
              <div className="relative bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 hover:bg-white/[0.08] transform hover:-translate-y-2 transition-all duration-300 group animate-slide-up">
                <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-secondary-500 to-secondary-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-secondary-500 to-secondary-600 shadow-md flex-shrink-0">
                    <svg
                      className="h-7 w-7 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    Main Building
                  </h3>
                </div>
                <p className="text-base leading-7 text-slate-400">
                  The main accommodation building features modern, comfortable
                  rooms designed for groups of children. Each room is equipped
                  with proper bedding, storage space, and heating. The building
                  includes shared bathroom facilities, common areas for group
                  activities, and is maintained to high cleanliness standards.
                </p>
              </div>

              {/* Summer Cottages */}
              <div
                className="relative bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 hover:bg-white/[0.08] transform hover:-translate-y-2 transition-all duration-300 group animate-slide-up"
                style={{ animationDelay: "0.1s" }}
              >
                <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-warm-500 to-warm-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-warm-500 to-warm-600 shadow-md flex-shrink-0">
                    <svg
                      className="h-7 w-7 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    Summer Cottages
                  </h3>
                </div>
                <p className="text-base leading-7 text-slate-400">
                  During the summer season, children stay in uniquely designed
                  cottages, each built in the architectural style of different
                  countries. These charming cottages provide a distinctive
                  cultural experience while offering comfortable sleeping
                  arrangements and a close connection to nature.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Dining & Medical (White) ───────────────── */}
      <section className="relative bg-white py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -left-32 w-[400px] h-[400px] rounded-full bg-primary-100/60 blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[350px] rounded-full bg-secondary-100/50 blur-[80px]" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Dining Services */}
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-sm font-medium text-primary-700 ring-1 ring-primary-200/50 mb-6">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
                Dining
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">
                Dining Services
              </h2>
              <div className="border-l-4 border-secondary-500 bg-neutral-50 rounded-xl p-6 pl-8">
                <p className="text-neutral-600 leading-relaxed">
                  Our professional catering team prepares nutritious,
                  well-balanced meals three times a day, plus snacks. Menus are
                  carefully planned by nutritionists to meet children's dietary
                  needs. We accommodate special dietary requirements including
                  allergies and cultural or religious dietary restrictions upon
                  advance request. Our modern kitchen and dining hall maintain
                  the highest food safety and hygiene standards.
                </p>
              </div>
            </div>

            {/* Medical & Safety */}
            <div className="animate-slide-up" style={{ animationDelay: "0.1s" }}>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-sm font-medium text-primary-700 ring-1 ring-primary-200/50 mb-6">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
                Safety
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">
                Medical & Safety
              </h2>
              <ul className="space-y-3">
                {medicalSafetyItems.map((item, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-3 text-neutral-600"
                  >
                    <svg
                      className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m4.5 12.75 6 6 9-13.5"
                      />
                    </svg>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Transportation & Facilities (Dark) ───────────────── */}
      <section className="relative overflow-hidden bg-primary-800">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-warm-500/15 blur-[120px]" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-secondary-500/10 blur-[100px]" />
        </div>
        <FloatingShapes variant="sparse" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Transportation */}
              <div className="animate-slide-up">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                  Transportation
                </h2>
                <div className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl p-8">
                  <p className="text-base leading-7 text-slate-400">
                    Located just 27 kilometers from Ulaanbaatar, the complex is
                    easily accessible. We provide organized transportation
                    services for groups, including airport and city transfers.
                    Our fleet of modern, well-maintained vehicles ensures safe
                    and comfortable travel for all campers and staff.
                  </p>
                </div>
              </div>

              {/* Facilities & Infrastructure */}
              <div
                className="animate-slide-up"
                style={{ animationDelay: "0.1s" }}
              >
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                  Facilities & Infrastructure
                </h2>
                <ul className="space-y-3">
                  {facilitiesItems.map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-slate-400"
                    >
                      <svg
                        className="h-5 w-5 text-secondary-400 mt-0.5 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="m4.5 12.75 6 6 9-13.5"
                        />
                      </svg>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Facilities;
